import balancesPanel from './balancesPanel.html';
import './balancesPanel.scss';

/*******************************************************************************
 * @ngdoc component
 * @name  csm.books.InitialSetup.dom.component:balancesPanel
 *
 * @param {boolean} forFiscalYear
 * For fiscal year flag. `false`=Tax year.
 *
 * @param {expr) onLoad
 * Evaluated when component is loaded. Passes `$validator` to main app to be
 * called when validation is required.
 *
 * @description
 * Initial Setup component for providing account forward balances when Fiscal/Tax
 * years are different or when opening date is different from start of one of the
 * periods. Fund Balance and Net Income are automatically calculated from Balance
 * Sheet accounts. Total Income/Expenses must match calculated Net Income. If
 * the totals don't match, the user must confirm an adjustment to Fund Balance to
 * bring the books into balance.
 *******************************************************************************/
export const balancesPanelComponent = {
  bindings: {
    forFiscalYear: '&',
    onLoad: '&'
  },
  template: balancesPanel,
  controller: controller
};

/*******************************************************************************
 * controller */
/**
 * Controller for the balances panel; sends its validator back to the main app.
 *******************************************************************************/
/* @ngInject */ function controller($injector, $scope, AppData) {
  var $ctrl = this;
  var $svcs = { $ctrl, $scope };

  /*----------------------------------*/
  /* Send validator back to main app. */
  /*----------------------------------*/
  $ctrl.$onInit = function () {
    $ctrl.calculateTotals = $injector.invoke(calculateTotals, null, $svcs);
    $ctrl.switchExpansion = $injector.invoke(switchExpansion, null, $svcs);
    $ctrl.validateTotals = $injector.invoke(validateTotals, null, $svcs);

    /*------------------------------------------------*/
    /* isBalanced - `true` when calculated net income */
    /* == total income/expense current balances.      */
    /*------------------------------------------------*/
    Object.defineProperties($ctrl, {
      isBalanced: {
        get: function () {
          return $ctrl.calcNetIncome === $ctrl.totalNetIncome;
        }
      }
    });

    /*--------------------------------------------------------------------*/
    /* Build grids and populate with PanelAccountTypes objects for        */
    /* editing of balance data. The grids are expandable to give the user */
    /* more room to view each one. Only one can be expanded at a time.    */
    /*--------------------------------------------------------------------*/
    $injector.invoke(buildGrids, null, $svcs);
    $ctrl.balanceSheetCollapsed = false;
    $ctrl.incomeExpenseCollapsed = true;
    $ctrl.bsGrid.data = AppData.bsAccounts;
    $ctrl.ieGrid.data = AppData.ieAccounts;

    $ctrl.calculateTotals();
    $ctrl.onLoad({ $validator: $ctrl.validateTotals });
  };
}

/*******************************************************************************
 * buildGrids */
/**
 * Creates `$ctrl.bsGrid` and `$ctrl.ieGrid` grids.
 *******************************************************************************/
/* @ngInject */ function buildGrids(
  $ctrl,
  $document,
  $scope,
  $timeout,
  uiGridConstants,
  AppConfig,
  AppData,
  AppTools,
  CommonUIGridTemplates,
  SERVER
) {
  /*-------------------------------------------------------*/
  /* Balance Sheet Accounts - Opening balance is editable, */
  /* current balance is not. No horizontal scrollbar.      */
  /*-------------------------------------------------------*/
  $ctrl.bsGrid = gridOptions(false, {
    minRowsToShow: 9,
    enableHorizontalScrollbar: uiGridConstants.scrollbars.NEVER
  });

  /*------------------------------------------------------*/
  /* Income Statement Accounts - Current balance is       */
  /* editable, opening balance is not. Include            */
  /* horizontal scrollbar and use custom header template. */
  /*------------------------------------------------------*/
  $ctrl.ieGrid = gridOptions(true, {
    minRowsToShow: 9,
    enableHorizontalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED
  });

  /*--------------------------------------------------------*/
  /* gridOptions - Returns grid options modified by         */
  /* `specs`. When `editCurrent` is `true`, current balance */
  /* is editable. Else, opening balance is editable.        */
  /*--------------------------------------------------------*/
  function gridOptions(editCurrent, specs) {
    return Object.assign(
      {
        enableCellEdit: false,
        enableCellEditOnFocus: true,
        enableColumnMenus: false,
        enableSorting: false,
        enableFiltering: true,

        enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
        onRegisterApi: function (api) {
          this.api = api;

          /*-------------------------------------------*/
          /* Calculate totals when cell value changes. */
          /*-------------------------------------------*/
          $scope.$on(
            'destroy',
            api.grid.registerDataChangeCallback($ctrl.calculateTotals, [
              uiGridConstants.dataChange.EDIT
            ])
          );
        },
        columnDefs: [
          /*----------------*/
          /* Account Number */
          /*----------------*/
          {
            displayName: 'Number',
            name: 'accountNumber',
            type: 'string',
            width: 100,
            enableSorting: true,
            allowCellFocus: false,
            visible: !AppConfig.hideAccountNumbers
          },
          /*--------------*/
          /* Account Name */
          /*--------------*/
          {
            displayName: SERVER.BooksWebConstants.accountLabel + ' Name',
            name: 'accountName',
            type: 'string',
            minWidth: 300,
            enableSorting: true,
            allowCellFocus: false
          },

          /*--------------*/
          /* Account Type */
          /*--------------*/
          {
            displayName: 'Type',
            name: 'type',
            enableSorting: true,
            type: 'string',
            width: 200,
            allowCellFocus: false
          },

          /*--------------------------------------------------------*/
          /* Opening Balance - Editable for Balance Sheet accounts. */
          /*--------------------------------------------------------*/
          {
            displayName:
              'Balance As Of\n' +
              dayBefore(
                $ctrl.forFiscalYear()
                  ? AppData.fyStartDate
                  : AppData.tyStartDate
              ),
            name: $ctrl.forFiscalYear()
              ? 'fyOpeningBalance'
              : 'tyOpeningBalance',
            type: 'number',
            cellClass: 'text-right',
            cellFilter: 'currency:"":2',
            headerCellClass: 'balance-header',
            tooltip: true,
            width: 130,
            enableFiltering: false,
            allowCellFocus: !editCurrent,
            enableCellEdit: !editCurrent,
            allowNegative: true,
            editableCellTemplate: CommonUIGridTemplates.CELL_NUMBER_INPUT,
            precision: 2
          },
          /*---------------------------------------------------------*/
          /* Current Balance - Editable for Income/Expense accounts. */
          /*---------------------------------------------------------*/
          {
            displayName:
              'Balance As Of\n' + dayBefore(AppData.orgInfo.openDate),
            name: $ctrl.forFiscalYear()
              ? 'fyCurrentBalance'
              : 'tyCurrentBalance',
            type: 'number',
            cellClass: 'text-right',
            cellFilter: 'currency:"":2',
            headerCellClass: 'balance-header',
            width: 130,
            enableFiltering: false,
            allowCellFocus: editCurrent,
            enableCellEdit: editCurrent,
            allowNegative: true,
            editableCellTemplate: CommonUIGridTemplates.CELL_NUMBER_INPUT,
            precision: 2
          }
        ]
      },
      specs
    );

    /*-----------------------------------------*/
    /* dayBefore - Returns date before `date`. */
    /*-----------------------------------------*/
    function dayBefore(date) {
      return AppTools.dateString(
        new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1)
      );
    }
  }
}

/*******************************************************************************
 * $ctrl.calculateTotals */
/**
 * Calculate Fund Balance, Net Income, and Total Income and Expenses. Called when
 * data is changed in the grid.
 *******************************************************************************/
/* @ngInject */ function calculateTotals($ctrl, AppData) {
  return function () {
    $ctrl.calcFundBalance = $ctrl.forFiscalYear()
      ? AppData.fyCalcFundBalance
      : AppData.tyCalcFundBalance;
    $ctrl.calcNetIncome = $ctrl.forFiscalYear()
      ? AppData.fyCalcNetIncome
      : AppData.tyCalcNetIncome;
    $ctrl.totalNetIncome = $ctrl.forFiscalYear()
      ? AppData.fyTotalNetIncome
      : AppData.tyTotalNetIncome;
  };
}

/*****************************************************************************
 * $ctrl.switchExpansion */
/**
 * Switches expansion state of Balance sheet and Income/Expense grids. This
 * way only one grid is expanded at a time so user can view more rows on each
 * grid.
 *****************************************************************************/
/* @ngInject */ function switchExpansion($ctrl) {
  return function () {
    $ctrl.balanceSheetCollapsed = !$ctrl.balanceSheetCollapsed;
    $ctrl.incomeExpenseCollapsed = !$ctrl.incomeExpenseCollapsed;
  };
}

/*******************************************************************************
 * $ctrl.validateTotals */
/**
 * Next button validator. Confirms calculated net income and total income and
 * expenses are equal. If not, allows the user to confirm an adjustment to Fund
 * Balance.
 *
 * Uses `SweetAlert2` for warning dialogs. Ok button cancels the option to make
 * the adjustment and Cancel button confirms adjustment should be made. Two
 * dialogs are shown making the user decide both times to proceed.
 *
 * @return {promise}  Resolved if totals match or user confirms adjustment.
 *******************************************************************************/
/* @ngInject */ function validateTotals(
  $ctrl,
  $filter,
  $q,
  AppData,
  AppServices
) {
  return function () {
    var fundBalanceAdj = $ctrl.forFiscalYear()
      ? AppData.fyFundBalanceAdj
      : AppData.tyFundBalanceAdj;

    return fundBalanceAdj === 0
      ? $q.resolve()
      : $q(function (resolve, reject) {
          var content1 =
            '<p>Your books do not balance with these amounts.</p>' +
            '<p>Please recheck the balances you entered<br>to make sure they are correct.</p>' +
            '<p>It is highly recommended that you do not proceed<br>with your books out of balance.</p>';

          var content2 =
            '<p>To bring these books into balance,<br><b>Fund Balance</b> must be adjusted by <b>' +
            $filter('currency')(fundBalanceAdj) +
            '</b>.</p>' +
            '<p>This is <b>NOT a good idea</b> because your books<br>will almost certainly be incorrect.</p>' +
            '<p>Are you certain you want to make this adjustment?</p>';

          AppServices.Alert.reverseConfirm(
            'Out of Balance',
            content1,
            'Recheck Balances',
            'Proceed Anyway'
          ).then(function () {
            AppServices.Alert.reverseConfirm(
              'Adjust Fund Balance',
              content2,
              'Nevermind',
              'Make the Adjustment'
            ).then(resolve, reject);
          }, reject);
        });
  };
}
